/**
 * Spec 031: the last policy text this browser showed, so the next visit can
 * paint it while the API is asked again. The key is per brand and kind.
 * Each site already has its own origin, so staging and production never mix.
 */

export type CachedPolicy = {
  versionId: string;
  versionNumber: number | null;
  effectiveDate: string;
  title: string;
  text: string;
  hash: string;
  approximateDates: boolean;
  savedAt: number;
};

export function policyCacheKey(brandSite: string, kind: string): string {
  return `policy_v1_${brandSite}_${kind}`;
}

function safeLocalStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function storageOf(
  storage: Pick<Storage, 'getItem' | 'removeItem' | 'setItem'> | null | undefined,
): Pick<Storage, 'getItem' | 'removeItem' | 'setItem'> | null {
  return storage === undefined ? safeLocalStorage() : storage;
}

function parseEntry(raw: string | null): CachedPolicy | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw);
    if (!value || typeof value !== 'object') return null;
    if (typeof value.versionId !== 'string' || !value.versionId) return null;
    if (typeof value.text !== 'string' || typeof value.hash !== 'string') return null;
    return {
      versionId: value.versionId,
      versionNumber: typeof value.versionNumber === 'number' ? value.versionNumber : null,
      effectiveDate: typeof value.effectiveDate === 'string' ? value.effectiveDate : '',
      title: typeof value.title === 'string' ? value.title : '',
      text: value.text,
      hash: value.hash,
      approximateDates: value.approximateDates === true,
      savedAt: typeof value.savedAt === 'number' ? value.savedAt : 0,
    };
  } catch {
    return null;
  }
}

/** SHA-256 hex of the policy text. Null when this environment cannot hash. */
export async function sha256Hex(text: string): Promise<string | null> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return null;
  const digest = await subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest))
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('');
}

export function readPolicyCache(
  brandSite: string,
  kind: string,
  storage?: Pick<Storage, 'getItem' | 'removeItem' | 'setItem'> | null,
): CachedPolicy | null {
  try {
    return parseEntry(storageOf(storage)?.getItem(policyCacheKey(brandSite, kind)) ?? null);
  } catch {
    return null;
  }
}

export function forgetPolicyCache(
  brandSite: string,
  kind: string,
  storage?: Pick<Storage, 'getItem' | 'removeItem' | 'setItem'> | null,
): void {
  try {
    storageOf(storage)?.removeItem(policyCacheKey(brandSite, kind));
  } catch {
    // Private mode: nothing to drop.
  }
}

/**
 * Read the cache and drop it when the stored hash is not the SHA-256 of the
 * text. A null hash (this environment cannot hash) keeps the entry.
 */
export async function loadPolicyCache(
  brandSite: string,
  kind: string,
  storage?: Pick<Storage, 'getItem' | 'removeItem' | 'setItem'> | null,
  hashText: (text: string) => Promise<string | null> = sha256Hex,
): Promise<CachedPolicy | null> {
  const entry = readPolicyCache(brandSite, kind, storage);
  if (!entry) return null;
  const hash = await hashText(entry.text);
  if (hash && hash !== entry.hash) {
    forgetPolicyCache(brandSite, kind, storage);
    return null;
  }
  return entry;
}

export function writePolicyCache(
  brandSite: string,
  kind: string,
  value: Omit<CachedPolicy, 'savedAt'>,
  storage?: Pick<Storage, 'getItem' | 'removeItem' | 'setItem'> | null,
  now: number = Date.now(),
): void {
  try {
    const entry: CachedPolicy = { ...value, savedAt: now };
    storageOf(storage)?.setItem(policyCacheKey(brandSite, kind), JSON.stringify(entry));
  } catch {
    // Private mode or a full quota: the page still works from the API.
  }
}
